import { useEffect, useRef, useState } from 'react';

import { apiUrl } from '@ai-studio/shared';

import { clock } from '../../../lib/loop-player.js';
import { IconButton } from '../../ui/controls.js';

/** Project media URL with HTTP range support (the server's /media route), so the browser can seek in an mp4. */
export function mediaUrl(projectId: number, path: string, version?: string): string {
  return apiUrl(`/projects/${projectId}/media?path=${encodeURIComponent(path)}${version ? `&v=${encodeURIComponent(version)}` : ''}`);
}

/**
 * A clip result: plays and loops (muted by default, so autoplay works), with play / pause, mute, a scrub bar
 * and frame-accurate time. `poster` shows the last frame until the clip loads.
 */
export function VideoPreview({ src, poster, label, autoPlay = true }: { src: string; poster?: string; label: string; autoPlay?: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setTime(0);
  }, [src]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => setPlaying(false));
    else v.pause();
  };

  return (
    <div className="gen-video" role="group" aria-label={`${label} player`}>
      <video
        ref={ref}
        className="gen-video-el"
        src={src}
        poster={poster}
        loop
        muted={muted}
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onError={() => setError('This clip cannot be played here (missing file or unsupported codec).')}
        onClick={toggle}
      />
      {error && <p className="gen-error">{error}</p>}
      <div className="gen-video-bar">
        <IconButton icon={playing ? 'pause' : 'play'} title={playing ? 'Pause' : 'Play (loops)'} active={playing} onClick={toggle} />
        <IconButton icon="audio" title={muted ? 'Unmute' : 'Mute'} active={!muted} onClick={() => setMuted((m) => !m)} />
        <input
          className="gen-video-scrub"
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={Math.min(time, duration || 0)}
          aria-label="Position"
          onChange={(e) => {
            const v = ref.current;
            if (v) v.currentTime = Number(e.target.value);
          }}
        />
        <span className="muted gen-loop-time">
          {clock(time)} / {clock(duration)}
        </span>
      </div>
    </div>
  );
}
